import React from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
} from "@material-ui/core";
import { useSnackbar } from "../utils/useSnackbar";
import { useCart } from "../Context/CartContext";
import { useOrderSession } from "../Context/OrderSessionContext";
import DeleteOutlineOutlinedIcon from "@material-ui/icons/DeleteOutlineOutlined";
import RemoveIcon from "@material-ui/icons/Remove";
import AddIcon from "@material-ui/icons/Add";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import RestaurantMenuOutlinedIcon from "@material-ui/icons/RestaurantMenuOutlined";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import NoteAddOutlinedIcon from "@material-ui/icons/NoteAddOutlined";
import ShoppingBasketOutlinedIcon from "@material-ui/icons/ShoppingBasketOutlined";
import TrendingDownOutlinedIcon from "@material-ui/icons/TrendingDownOutlined";
import TouchRipple from "@material-ui/core/ButtonBase";
import { makeStyles } from "@material-ui/core/styles";
import useStyles from "./CartStyles";
import usePermissions from "../utils/permissions";
import PermissionTooltip from "../components/PermissionTooltip/PermissionTooltip";
import { useEffectiveProfile } from "../utils/useEffectiveProfile";
import { useConfirmation } from "../Context/ConfirmationContext";
import { updateItem } from "../actions/itemActions";

// Leftover pre-react-toastify options - "bottom" isn't a valid
// react-toastify position (see PaymentDetails.js), and the style/closeStyle
// fields are silently ignored too (see ItemCards.js).

const CartTable = ({ currency }) => {
  const classes = useStyles();
  // Was: shown unconditionally, even when this business has the Hold/KOT
  // workflow turned off entirely (Cart.js already gates the Hold Bill/KOT
  // buttons themselves on this same flag - see holdFeatureEnabled there).
  // Without Hold KOT enabled there's no kitchen ticket for this remark to
  // travel with, so the icon was just clutter on every cart row.
  const effectiveProfile = useEffectiveProfile();
  const holdFeatureEnabled = !!effectiveProfile?.enableHoldKot;
  const showStockVisible = !!(effectiveProfile?.showStoke || effectiveProfile?.showStock);
  // Special Feature, default OFF (see GlobalSettingsModel.js) - when on, an
  // explicit price override also updates the item's master/catalog price,
  // after the cashier confirms (see handleSavePriceDialog below).
  const lockItemPriceOverrides = effectiveProfile?.lockItemPriceOverrides === true;
  const dispatch = useDispatch();
  const catalogItems = useSelector((state) => state.items.items);
  const confirmDialog = useConfirmation();
  const [openSnackbar, closeSnackbar] = useSnackbar();
  const { isAdmin, canOverridePrice, loading: permissionsLoading } = usePermissions();
  const priceEditDisabled = permissionsLoading || (!isAdmin() && !canOverridePrice());
  const [sessionQtyDialogOpen, setSessionQtyDialogOpen] = React.useState(false);
  const [editingSessionIndex, setEditingSessionIndex] = React.useState(null);
  const [editingSessionQty, setEditingSessionQty] = React.useState(1);
  const [priceDialogOpen, setPriceDialogOpen] = React.useState(false);
  const [priceDialogTarget, setPriceDialogTarget] = React.useState(null); // { type: 'session'|'cart', index }
  const [priceDialogValue, setPriceDialogValue] = React.useState("");
  const [remarkDialogOpen, setRemarkDialogOpen] = React.useState(false);
  const [remarkDialogTarget, setRemarkDialogTarget] = React.useState(null); // { type: 'session', index }
  const [remarkDialogValue, setRemarkDialogValue] = React.useState("");
  const [noteDialogOpen, setNoteDialogOpen] = React.useState(false);
  const [noteDialogTarget, setNoteDialogTarget] = React.useState(null); // { type: 'session'|'cart', index }
  const [noteDialogValue, setNoteDialogValue] = React.useState("");

  const formatQuantity = (value) => {
    const parsed = parseFloat(value);
    if (Number.isNaN(parsed)) return "0";
    return parsed.toFixed(3).replace(/\.?0+$/, "");
  };
  const {
    activeSessionId,
    activeSession,
    removeItemFromSession,
    updateItemInSession,
    refreshActiveSession,
  } = useOrderSession();

  const isItemPrintedInKOT = (itemId) => {
    if (!activeSession || !activeSession.items) return false;
    const sessionItem = activeSession.items.find(
      (item) => (item.itemId?._id || item.itemId) === itemId
    );
    return sessionItem?.kotPrinted || false;
  };

  const applySessionPriceChange = async (index, rawValue) => {
    if (!activeSessionId || !activeSession?.items?.[index]) return;
    const sessionItem = activeSession.items[index];
    const baseRate =
      sessionItem.originalRate ??
      sessionItem.itemId?.units?.sale?.price ??
      sessionItem.rate ??
      0;
    let parsed = parseFloat(rawValue);
    if (rawValue === "" || rawValue == null || Number.isNaN(parsed) || parsed <= 0) {
      parsed = baseRate;
    }
    const priceAdjusted = Math.abs(parsed - baseRate) > 0.0001;
    try {
      await updateItemInSession(activeSessionId, {
        index,
        rate: parsed,
        originalRate: baseRate,
        priceAdjusted,
      });
      if (typeof refreshActiveSession === "function") {
        await refreshActiveSession();
      }
    } catch (error) {
      console.error("Failed to update session item price:", error);
    }
  };

  const handleOpenPriceDialog = (type, index, currentPrice) => {
    setPriceDialogTarget({ type, index, currentPrice });
    setPriceDialogValue(
      currentPrice != null && !Number.isNaN(currentPrice) ? String(currentPrice) : ""
    );
    setPriceDialogOpen(true);
  };

  const handleClosePriceDialog = () => {
    setPriceDialogOpen(false);
    setPriceDialogTarget(null);
    setPriceDialogValue("");
  };

  // "Lock Item Price Overrides" Special Feature (see GlobalSettingsModel.js)
  // - when on, confirming an actual price change here also updates the
  // item's master/catalog price (units.sale.price), after the cashier
  // confirms it's intentional. Declining the confirmation still applies the
  // change to this bill only - it just skips the catalog write.
  const maybeUpdateMasterPrice = async (itemId, itemName, baseRate, newValue) => {
    if (!lockItemPriceOverrides || !itemId || newValue == null) return;
    if (Math.abs(newValue - (baseRate || 0)) <= 0.0001) return; // not actually a change

    const fullCatalogItem = catalogItems.find((it) => it._id === itemId);
    if (!fullCatalogItem) return;

    const ok = await confirmDialog({
      title: "Update Item Price?",
      message: `This will also update ${itemName || fullCatalogItem.itemName}'s master price to ${currency}${newValue} for all future bills, not just this one. Continue?`,
      confirmLabel: "Update Price",
      cancelLabel: "Keep Bill-Only",
      confirmColor: "#ea580c",
    });
    if (!ok) return;

    // Full-object spread required, not optional - itemUpdateById
    // (server/controllers/items.js) does a shallow findByIdAndUpdate with
    // whatever's in the request body, so a partial { units: { sale: {...} } }
    // payload would silently wipe out units.purchase/units.stock.
    dispatch(
      updateItem(
        itemId,
        {
          ...fullCatalogItem,
          units: {
            ...fullCatalogItem.units,
            sale: { ...fullCatalogItem.units.sale, price: newValue },
          },
        },
        openSnackbar
      )
    );
  };

  const handleSavePriceDialog = async () => {
    if (!priceDialogTarget) {
      handleClosePriceDialog();
      return;
    }
    const parsed = parseFloat(priceDialogValue);
    const value =
      priceDialogValue === "" || priceDialogValue == null || Number.isNaN(parsed) || parsed <= 0
        ? null
        : parsed;
    if (priceDialogTarget.type === "session") {
      const sessionItem = activeSession?.items?.[priceDialogTarget.index];
      const itemId = sessionItem?.itemId?._id || sessionItem?.itemId;
      const baseRate =
        sessionItem?.originalRate ??
        sessionItem?.itemId?.units?.sale?.price ??
        sessionItem?.rate ??
        0;
      await maybeUpdateMasterPrice(itemId, sessionItem?.name, baseRate, value);
      await applySessionPriceChange(priceDialogTarget.index, value);
    } else if (priceDialogTarget.type === "cart") {
      const cartItem = cartItems[priceDialogTarget.index];
      await maybeUpdateMasterPrice(
        cartItem?._id,
        cartItem?.itemName,
        getOriginalUnitPrice(cartItem),
        value
      );
      await updateItemPrice(priceDialogTarget.index, value);
    }
    handleClosePriceDialog();
  };

  const handleOpenRemarkDialog = (type, index, currentRemark) => {
    setRemarkDialogTarget({ type, index });
    setRemarkDialogValue(currentRemark || "");
    setRemarkDialogOpen(true);
  };

  const handleCloseRemarkDialog = () => {
    setRemarkDialogOpen(false);
    setRemarkDialogTarget(null);
    setRemarkDialogValue("");
  };

  const handleSaveRemarkDialog = async () => {
    if (!remarkDialogTarget) {
      handleCloseRemarkDialog();
      return;
    }
    try {
      if (remarkDialogTarget.type === "session" && activeSessionId) {
        await updateItemInSession(activeSessionId, {
          index: remarkDialogTarget.index,
          remark: remarkDialogValue.trim() || null,
        });
        if (typeof refreshActiveSession === "function") {
          await refreshActiveSession();
        }
      } else if (remarkDialogTarget.type === "cart") {
        const updated = [...cartItems];
        if (remarkDialogTarget.index >= 0 && remarkDialogTarget.index < updated.length) {
          updated[remarkDialogTarget.index] = {
            ...updated[remarkDialogTarget.index],
            remark: remarkDialogValue.trim() || null,
          };
          setCartItems(updated);
        }
      }
      handleCloseRemarkDialog();
    } catch (error) {
      console.error("Failed to update item remark:", error);
    }
  };

  const handleOpenNoteDialog = (type, index, currentNote) => {
    setNoteDialogTarget({ type, index });
    setNoteDialogValue(currentNote || "");
    setNoteDialogOpen(true);
  };

  const handleCloseNoteDialog = () => {
    setNoteDialogOpen(false);
    setNoteDialogTarget(null);
    setNoteDialogValue("");
  };

  const handleSaveNoteDialog = async () => {
    if (!noteDialogTarget) {
      handleCloseNoteDialog();
      return;
    }
    try {
      if (noteDialogTarget.type === "session" && activeSessionId) {
        await updateItemInSession(activeSessionId, {
          index: noteDialogTarget.index,
          customerNote: noteDialogValue.trim() || null,
        });
        if (typeof refreshActiveSession === "function") {
          await refreshActiveSession();
        }
      } else if (noteDialogTarget.type === "cart") {
        const updated = [...cartItems];
        if (noteDialogTarget.index >= 0 && noteDialogTarget.index < updated.length) {
          updated[noteDialogTarget.index] = {
            ...updated[noteDialogTarget.index],
            customerNote: noteDialogValue.trim() || null,
          };
          setCartItems(updated);
        }
      }
      handleCloseNoteDialog();
    } catch (error) {
      console.error("Failed to update item invoice note:", error);
    }
  };

  const {
    cartItems,
    selectedIndex,
    addToCart,
    removeFromCart,
    incrementQuantity,
    decrementQuantity,
    getTotalPrice,
    getSubTotalPrice,
    calculatePrice,
    handleOpenQtyDialog,
    handleCloseQtyDialog,
    QtydialogOpen,
    handleQuantityChange,
    calculateVat,
    subTotal,
    setSubTotal,
    setCartItems,
    vat,
    setVat,
    updateItemPrice,
    getEffectiveUnitPrice,
    getOriginalUnitPrice,
    isItemPriceAdjusted,
    getItemMinSaleQty,
    showMinSaleQtyWarning,
  } = useCart();

  // Cart rows render newest-first (see the .reverse() below), so the most
  // recently added item is always the top row - but a shopper who's scrolled
  // down a long cart would still miss it. Scrolling the list back to the top
  // whenever an item is added closes that gap. Only fires on growth (not on
  // removal/edit) by comparing against the previous length, so deleting or
  // adjusting a mid-list item never yanks the scroll position out from under
  // the person doing that edit.
  const tableWrapperRef = React.useRef(null);
  const previousCartLengthRef = React.useRef(cartItems.length);
  React.useEffect(() => {
    if (cartItems.length > previousCartLengthRef.current) {
      tableWrapperRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }
    previousCartLengthRef.current = cartItems.length;
  }, [cartItems.length]);

  return (
    <div className={classes.tableWrapper} ref={tableWrapperRef}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow className={classes.tableRow}>
            <TableCell className={classes.tableHead}>Item Name</TableCell>
            <TableCell className={classes.tableHead} align="center">Qty</TableCell>
            <TableCell className={classes.tableHead} align="right">Price</TableCell>
            <TableCell className={classes.tableHead} align="right">Amount</TableCell>
            <TableCell className={classes.tableHead} align="center">Delete</TableCell>
          </TableRow>
        </TableHead>
        <TableBody className={classes.tableBody}>
          {activeSessionId && activeSession?.items?.length
            ? activeSession.items.map((si, idx) => {
                const isPrinted = si.kotPrinted;
                const itemId = si.itemId?._id || si.itemId;
                const unitAbbr = si.itemId?.units?.sale?.unit?.abbreviation || "";
                return (
                  <TableRow
                    key={`session-${idx}`}
                    style={{
                      backgroundColor: isPrinted ? "#f1f8e9" : "#fff3e0",
                      borderTop: !isPrinted ? "2px solid #ff9800" : undefined,
                    }}
                  >
                    <TableCell className={classes.tableCell}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <span>{si.name}</span>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenRemarkDialog("session", idx, si.remark)}
                          style={{
                            padding: "4px",
                            color: si.remark ? "#ff9800" : "#999",
                          }}
                          title={si.remark ? `Remark: ${si.remark}` : "Add remark for kitchen"}
                        >
                          <DescriptionOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenNoteDialog("session", idx, si.customerNote)}
                          style={{
                            padding: "4px",
                            color: si.customerNote ? "#1976d2" : "#999",
                          }}
                          title={si.customerNote ? `Invoice note: ${si.customerNote}` : "Add note for invoice"}
                        >
                          <NoteAddOutlinedIcon fontSize="small" />
                        </IconButton>
                        {isPrinted ? (
                          <Chip
                            icon={<RestaurantMenuOutlinedIcon style={{ fontSize: 12, color: "white" }} />}
                            label="In Kitchen"
                            size="small"
                            style={{
                              height: 20,
                              fontSize: 10,
                              backgroundColor: "#16a34a",
                              color: "white",
                              fontWeight: 600,
                            }}
                          />
                        ) : (
                          <Chip
                            label="New"
                            size="small"
                            style={{
                              height: 20,
                              fontSize: 10,
                              backgroundColor: "#ea580c",
                              color: "white",
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </div>
                    </TableCell>
                    <TableCell
                      className={classes.tableCell}
                      align="center"
                      style={{
                        cursor: "pointer",
                        position: "relative",
                        overflow: "hidden",
                      }}
                      onClick={() => {
                        setEditingSessionIndex(idx);
                        setEditingSessionQty(si.qty);
                        setSessionQtyDialogOpen(true);
                      }}
                    >
                      <TouchRipple center="true" />
                      <div className={classes.qtyPill}>
                        <span>{formatQuantity(si.qty)}</span>
                        {unitAbbr && (
                          <span style={{ fontSize: "0.7rem", color: "#64748b", fontWeight: 500 }}>
                            {unitAbbr}
                          </span>
                        )}
                      </div>
                      <TouchRipple />
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {(() => {
                        const baseRate =
                          si.originalRate ??
                          si.itemId?.units?.sale?.price ??
                          si.rate ??
                          0;
                        const effectiveRate = si.rate ?? baseRate;
                        const priceAdjusted =
                          si.priceAdjusted || Math.abs(effectiveRate - baseRate) > 0.0001;
                        return (
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                            <div style={{ 
                              display: "flex", 
                              alignItems: "center", 
                              gap: 6,
                              padding: "2px 6px",
                              borderRadius: "4px",
                              backgroundColor: priceAdjusted ? "#fff7ed" : "transparent",
                              transition: "all 0.2s ease"
                            }}>
                              <span style={{ 
                                fontSize: "14px", 
                                fontWeight: priceAdjusted ? 700 : 600,
                                color: priceAdjusted ? "#ea580c" : "#1e293b"
                              }}>
                                {`${currency}${effectiveRate.toFixed(2)}`}
                              </span>
                              <PermissionTooltip disabled={priceEditDisabled} enabledTitle="Override price">
                                <IconButton
                                  size="small"
                                  disabled={priceEditDisabled}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenPriceDialog("session", idx, effectiveRate);
                                  }}
                                  style={{
                                    padding: "3px",
                                    backgroundColor: "#f1f5f9",
                                    borderRadius: "6px",
                                    transition: "all 0.2s ease"
                                  }}
                                >
                                  <EditOutlinedIcon style={{ color: "#64748b", fontSize: "14px" }} />
                                </IconButton>
                              </PermissionTooltip>
                            </div>
                            {priceAdjusted && (
                              <Typography
                                variant="caption"
                                style={{ 
                                  textDecoration: "line-through",
                                  color: "#94a3b8",
                                  fontSize: "11px"
                                }}
                              >
                                {`${currency}${baseRate.toFixed(2)}`}
                              </Typography>
                            )}
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {(() => {
                        const baseRate =
                          si.originalRate ??
                          si.itemId?.units?.sale?.price ??
                          si.rate ??
                          0;
                        const effectiveRate = si.rate ?? baseRate;
                        const priceAdjusted =
                          si.priceAdjusted ||
                          Math.abs(effectiveRate - baseRate) > 0.0001;
                        const amount = (si.qty || 0) * effectiveRate;
                        const originalAmount = (si.qty || 0) * baseRate;
                        return (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                              gap: 2
                            }}
                          >
                            <span style={{ 
                              fontSize: "14px", 
                              fontWeight: 700,
                              color: priceAdjusted ? "#ea580c" : "#0f172a"
                            }}>
                              {`${currency}${amount.toFixed(2)}`}
                            </span>
                            {priceAdjusted && (
                              <Typography
                                variant="caption"
                                style={{
                                  color: "#94a3b8",
                                  fontSize: "11px",
                                  textDecoration: "line-through"
                                }}
                              >
                                Orig: {currency}{originalAmount.toFixed(2)}
                              </Typography>
                            )}
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell className={classes.tableCell} style={{ textAlign: "center" }}>
                      <IconButton
                        size="small"
                        className={classes.deleteIcon}
                        onClick={async () => {
                          try {
                            await removeItemFromSession(activeSessionId, { index: idx });
                          } catch (e) {
                            console.error(e);
                          }
                        }}
                      >
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            : cartItems.length ? cartItems.map((item, index) => {
                const isPrinted = isItemPrintedInKOT(item._id);
                // Only THIS row is affected - every other cart item's
                // appearance/logic is untouched by this flag.
                const isNegativeStock = !!item.isNegativeStock;
                return (
                  <TableRow
                    key={index}
                    style={{
                      backgroundColor: isNegativeStock ? "#fdecea" : (isPrinted ? "#f1f8e9" : "transparent"),
                      borderLeft: isNegativeStock ? "4px solid #dc2626" : "4px solid transparent",
                    }}
                  >
              <TableCell className={classes.tableCell}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span>{item.itemName}</span>
                        {isNegativeStock && (
                          <Chip
                            icon={<TrendingDownOutlinedIcon style={{ fontSize: 12, color: "white" }} />}
                            label="Negative Stock"
                            size="small"
                            style={{
                              height: 20,
                              fontSize: 10,
                              backgroundColor: "#dc2626",
                              color: "white",
                            }}
                          />
                        )}
                        {holdFeatureEnabled && (
                          <IconButton
                            size="small"
                            onClick={() => handleOpenRemarkDialog("cart", index, item.remark)}
                            style={{
                              padding: "4px",
                              color: item.remark ? "#ff9800" : "#999",
                            }}
                            title={item.remark ? `Remark: ${item.remark}` : "Add remark for kitchen"}
                          >
                            <DescriptionOutlinedIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleOpenNoteDialog("cart", index, item.customerNote)}
                          style={{
                            padding: "4px",
                            color: item.customerNote ? "#1976d2" : "#999",
                          }}
                          title={item.customerNote ? `Invoice note: ${item.customerNote}` : "Add note for invoice"}
                        >
                          <NoteAddOutlinedIcon fontSize="small" />
                        </IconButton>
                        {isPrinted && (
                          <Chip
                            icon={<RestaurantMenuOutlinedIcon style={{ fontSize: 12, color: "white" }} />}
                            label="In Kitchen"
                            size="small"
                            style={{
                              height: 20,
                              fontSize: 10,
                              backgroundColor: "#16a34a",
                              color: "white",
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </div>
                      {isNegativeStock && showStockVisible && (
                        <Typography variant="caption" style={{ color: "#dc2626", fontWeight: 700, fontSize: 11 }}>
                          {`Stock: ${Number(item.stockAfterSale || 0).toFixed(2)} ${item?.units?.stock?.unit?.abbreviation || ""}`}
                        </Typography>
                      )}
                      </div>
              </TableCell>
              <TableCell
                className={classes.tableCell}
                align="center"
                style={{
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                }}
                onClick={() => handleOpenQtyDialog(index)}
              >
                <TouchRipple center="true" />
                <div className={classes.qtyPill}>
                  <span>{formatQuantity(item.quantity)}</span>
                  {item?.units?.sale?.unit?.abbreviation && (
                    <span style={{ fontSize: "0.7rem", color: "#64748b", fontWeight: 500 }}>
                      {item.units.sale.unit.abbreviation}
                    </span>
                  )}
                </div>
                <TouchRipple />
              </TableCell>
              <TableCell className={classes.tableCell}>
                      {(() => {
                        const effectivePrice = getEffectiveUnitPrice(item);
                        const originalPrice = getOriginalUnitPrice(item);
                        const priceAdjusted = isItemPriceAdjusted(item);
                        return (
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                            <div style={{ 
                              display: "flex", 
                              alignItems: "center", 
                              gap: 6,
                              padding: "2px 6px",
                              borderRadius: "4px",
                              backgroundColor: priceAdjusted ? "#fff7ed" : "transparent",
                              transition: "all 0.2s ease"
                            }}>
                              <span style={{ 
                                fontSize: "14px", 
                                fontWeight: priceAdjusted ? 700 : 600,
                                color: priceAdjusted ? "#ea580c" : "#1e293b"
                              }}>
                                {`${currency}${effectivePrice.toFixed(2)}`}
                              </span>
                              <PermissionTooltip disabled={priceEditDisabled} enabledTitle="Override price">
                                <IconButton
                                  size="small"
                                  disabled={priceEditDisabled}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenPriceDialog("cart", index, effectivePrice);
                                  }}
                                  style={{
                                    padding: "3px",
                                    backgroundColor: "#f1f5f9",
                                    borderRadius: "6px",
                                    transition: "all 0.2s ease"
                                  }}
                                >
                                  <EditOutlinedIcon style={{ color: "#64748b", fontSize: "14px" }} />
                                </IconButton>
                              </PermissionTooltip>
                            </div>
                            {priceAdjusted && (
                              <Typography
                                variant="caption"
                                style={{ 
                                  textDecoration: "line-through",
                                  color: "#94a3b8",
                                  fontSize: "11px"
                                }}
                              >
                                {`${currency}${originalPrice.toFixed(2)}`}
                              </Typography>
                            )}
                          </div>
                        );
                      })()}
              </TableCell>
              <TableCell className={classes.tableCell}>
                      {(() => {
                        const amount = calculatePrice(item);
                        const originalAmount = (item.quantity || 0) * getOriginalUnitPrice(item);
                        const priceAdjusted = isItemPriceAdjusted(item);
                        return (
                          <div style={{ 
                            display: "flex", 
                            flexDirection: "column", 
                            alignItems: "flex-end",
                            gap: 2
                          }}>
                            <span style={{ 
                              fontSize: "14px", 
                              fontWeight: 700,
                              color: priceAdjusted ? "#ea580c" : "#0f172a"
                            }}>
                              {`${currency}${amount}`}
                            </span>
                            {priceAdjusted && (
                              <Typography 
                                variant="caption" 
                                style={{
                                  color: "#94a3b8",
                                  fontSize: "11px",
                                  textDecoration: "line-through"
                                }}
                              >
                                Orig: {currency}{originalAmount.toFixed(2)}
                              </Typography>
                            )}
                          </div>
                        );
                      })()}
              </TableCell>
              <TableCell className={classes.tableCell} style={{ textAlign: "center" }}>
                <IconButton
                  size="small"
                  className={classes.deleteIcon}
                  onClick={async () => {
                    if (activeSessionId && item._id) {
                      try {
                        await removeItemFromSession(activeSessionId, item._id);
                      } catch (e) {
                        console.error(e);
                      }
                    }
                    removeFromCart(index);
                  }}
                >
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
                );
              // Newest item first: the map above still runs left-to-right over
              // the real cartItems array, so every closure still captures its
              // item's true index (edit/delete/qty handlers stay correct) -
              // only the finished array of rows gets flipped for display.
              }).reverse() : (
                <TableRow>
                  <TableCell colSpan={5} style={{ padding: 0, borderBottom: 0 }}>
                    <div className={classes.emptyCart}>
                      <div className={classes.emptyCartIconBox}>
                        <ShoppingBasketOutlinedIcon className={classes.emptyCartIcon} />
                      </div>
                      <Typography className={classes.emptyCartTitle}>
                        Your cart is empty
                      </Typography>
                      <Typography className={classes.emptyCartSubtitle}>
                        Select products to build this order.
                      </Typography>
                    </div>
                  </TableCell>
                </TableRow>
              )}
        </TableBody>

        <Dialog
          open={QtydialogOpen}
          onClose={handleCloseQtyDialog}
          classes={{ paper: classes.dialog }}
        >
          <DialogTitle className={classes.dialogTitle}>
            Adjust Quantity
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            {cartItems.length > 0 &&
            selectedIndex >= 0 &&
            selectedIndex < cartItems.length ? (
              <div className={classes.pillContainer}>
                <Typography variant="h6" className={classes.itemText}>
                  {cartItems[selectedIndex].itemName || cartItems[selectedIndex].name || "Item"}
                </Typography>
                <span className={classes.unitBadge}>
                  Quantity in{" "}
                  {cartItems[selectedIndex]?.units?.sale?.unit?.name ||
                    cartItems[selectedIndex]?.units?.sale?.unit?.abbreviation ||
                    cartItems[selectedIndex]?.unitLabel ||
                    "unit"}
                </span>
                <div className={classes.pillButtonGroup}>
                  <IconButton
                    className={classes.pillIconButton}
                    onClick={async () => {
                      const it = cartItems[selectedIndex];
                      const floor = getItemMinSaleQty(it) || 1;
                      if (activeSessionId && it?._id && parseFloat(it.quantity) > floor) {
                        try {
                          await updateItemInSession(activeSessionId, it._id, parseFloat(it.quantity) - 1);
                        } catch (e) {
                          console.error(e);
                        }
                      }
                      decrementQuantity(selectedIndex);
                    }}
                    disabled={parseFloat(cartItems[selectedIndex].quantity || 0) <= (getItemMinSaleQty(cartItems[selectedIndex]) || 1)}
                  >
                    <RemoveIcon style={{ width: "1.3rem", height: "1.3rem" }} />
                  </IconButton>

                  <TextField
                    className={classes.quantityInput}
                    type="number"
                    value={cartItems[selectedIndex].quantity}
                    onChange={async (event) => {
                      const it = cartItems[selectedIndex];
                      const newQty = parseFloat(event.target.value);
                      // Minimum Sale Quantity Special Feature - must be
                      // checked here too, BEFORE mirroring into the
                      // session: handleQuantityChange below already rejects
                      // a too-low value for the local cart, but that
                      // rejection happens too late to stop this session
                      // sync from already having pushed the bad value.
                      const minSaleQty = getItemMinSaleQty(it);
                      if (minSaleQty != null && !Number.isNaN(newQty) && newQty < minSaleQty) {
                        showMinSaleQtyWarning(it?.itemName, minSaleQty, it?.units?.sale?.unit?.abbreviation || "");
                        return;
                      }
                      if (activeSessionId && it?._id && !Number.isNaN(newQty) && newQty > 0) {
                        try {
                          await updateItemInSession(activeSessionId, it._id, newQty);
                        } catch (e) {
                          console.error(e);
                        }
                      }
                      handleQuantityChange(event, selectedIndex, openSnackbar);
                    }}
                    variant="outlined"
                    inputProps={{
                      min: 0.001,
                      step: 0.001,
                      style: { textAlign: "center" },
                    }}
                    onFocus={(event) => event.target.select()}
                  />

                  <IconButton
                    className={classes.pillIconButton}
                    onClick={async () => {
                      const it = cartItems[selectedIndex];
                      if (activeSessionId && it?._id) {
                        try {
                          await updateItemInSession(activeSessionId, it._id, (parseFloat(it.quantity) || 0) + 1);
                        } catch (e) {
                          console.error(e);
                        }
                      }
                      incrementQuantity(selectedIndex, openSnackbar);
                    }}
                    // disabled={
                    //   cartItems[selectedIndex].toggleStock &&
                    //   cartItems[selectedIndex].quantity >=
                    //     cartItems[selectedIndex].stockQty.currentQty
                    // }
                  >
                    <AddIcon style={{ width: "1.3rem", height: "1.3rem" }} />
                  </IconButton>
                </div>
              </div>
            ) : (
              <div>No item selected</div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={sessionQtyDialogOpen}
          onClose={() => setSessionQtyDialogOpen(false)}
          classes={{ paper: classes.dialog }}
        >
          <DialogTitle className={classes.dialogTitle}>Edit quantity</DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <Typography className={classes.itemText}>
              {editingSessionIndex != null && activeSession?.items?.[editingSessionIndex]
                ? activeSession.items[editingSessionIndex].name
                : ""}
            </Typography>
            <div className={classes.pillButtonGroup}>
              <IconButton
                className={classes.pillIconButton}
                onClick={() => {
                  const floor = getItemMinSaleQty(activeSession?.items?.[editingSessionIndex]?.itemId) || 1;
                  setEditingSessionQty((q) => {
                    if (q - 1 < floor) {
                      showMinSaleQtyWarning(
                        activeSession?.items?.[editingSessionIndex]?.name,
                        floor,
                        activeSession?.items?.[editingSessionIndex]?.itemId?.units?.sale?.unit?.abbreviation || ""
                      );
                      return q;
                    }
                    return q - 1;
                  });
                }}
                disabled={editingSessionQty <= (getItemMinSaleQty(activeSession?.items?.[editingSessionIndex]?.itemId) || 1)}
              >
                <RemoveIcon style={{ width: "2em", height: "2em" }} />
              </IconButton>
              <TextField
                className={classes.quantityInput}
                type="number"
                value={editingSessionQty}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (Number.isNaN(v) || v <= 0) return;
                  const floor = getItemMinSaleQty(activeSession?.items?.[editingSessionIndex]?.itemId);
                  if (floor != null && v < floor) {
                    showMinSaleQtyWarning(
                      activeSession?.items?.[editingSessionIndex]?.name,
                      floor,
                      activeSession?.items?.[editingSessionIndex]?.itemId?.units?.sale?.unit?.abbreviation || ""
                    );
                    return;
                  }
                  setEditingSessionQty(v);
                }}
                variant="outlined"
                inputProps={{ min: 0.001, step: 0.001, style: { textAlign: "center" } }}
                onFocus={(e) => e.target.select()}
              />
              <IconButton
                className={classes.pillIconButton}
                onClick={() => setEditingSessionQty((q) => q + 1)}
              >
                <AddIcon style={{ width: "2em", height: "2em" }} />
              </IconButton>
            </div>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              style={{ marginTop: 16 }}
              onClick={async () => {
                if (activeSessionId == null || editingSessionIndex == null) {
                  setSessionQtyDialogOpen(false);
                  return;
                }
                try {
                  await updateItemInSession(activeSessionId, {
                    index: editingSessionIndex,
                    qty: editingSessionQty,
                  });
                  setSessionQtyDialogOpen(false);
                } catch (e) {
                  console.error(e);
                }
              }}
            >
              Save
            </Button>
          </DialogContent>
        </Dialog>

        <Dialog
          open={priceDialogOpen}
          onClose={handleClosePriceDialog}
          classes={{ paper: classes.dialog }}
        >
          <DialogTitle className={classes.dialogTitle}>Adjust Price</DialogTitle>
          <DialogContent className={classes.dialogContent}>
            {priceDialogTarget && (() => {
              let originalPrice = 0;
              let itemName = "";
              if (priceDialogTarget.type === "session" && activeSession?.items?.[priceDialogTarget.index]) {
                const si = activeSession.items[priceDialogTarget.index];
                originalPrice = si.originalRate ?? si.itemId?.units?.sale?.price ?? si.rate ?? 0;
                itemName = si.name || "";
              } else if (priceDialogTarget.type === "cart" && cartItems[priceDialogTarget.index]) {
                const item = cartItems[priceDialogTarget.index];
                originalPrice = getOriginalUnitPrice(item);
                itemName = item.itemName || "";
              }
              const isAdjusted = priceDialogTarget.currentPrice && Math.abs(priceDialogTarget.currentPrice - originalPrice) > 0.0001;
              return (
                <>
                  {itemName && (
                    <Typography variant="subtitle2" style={{ marginBottom: 12, fontWeight: 600 }}>
                      {itemName}
                    </Typography>
                  )}
                  <div style={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center",
                    marginBottom: 16,
                    padding: "8px 12px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "4px"
                  }}>
                    <Typography variant="body2" color="textSecondary">
                      Original Price:
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      {`${currency}${originalPrice.toFixed(2)}`}
                    </Typography>
                  </div>
                  <TextField
                    type="number"
                    label="New Price"
                    value={priceDialogValue}
                    onChange={(e) => setPriceDialogValue(e.target.value)}
                    variant="outlined"
                    fullWidth
                    inputProps={{ min: 0, step: "0.01" }}
                    style={{ marginBottom: 8 }}
                    autoFocus
                  />
                  <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
                    Leave empty or enter 0 to restore original price.
                  </Typography>
                </>
              );
            })()}
          </DialogContent>
          <DialogActions style={{ padding: "8px 16px 16px" }}>
            <Button onClick={handleClosePriceDialog}>Cancel</Button>
            <Button color="primary" variant="contained" onClick={handleSavePriceDialog}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={remarkDialogOpen}
          onClose={handleCloseRemarkDialog}
          classes={{ paper: classes.dialog }}
        >
          <DialogTitle className={classes.dialogTitle}>Add Remark for Kitchen</DialogTitle>
          <DialogContent className={classes.dialogContent}>
            {remarkDialogTarget && (() => {
              let itemName = "";
              if (remarkDialogTarget.type === "session" && activeSession?.items?.[remarkDialogTarget.index]) {
                itemName = activeSession.items[remarkDialogTarget.index].name || "";
              } else if (remarkDialogTarget.type === "cart" && cartItems?.[remarkDialogTarget.index]) {
                itemName = cartItems[remarkDialogTarget.index].itemName || cartItems[remarkDialogTarget.index].name || "";
              }
              return (
                <>
                  {itemName && (
                    <Typography variant="subtitle2" style={{ marginBottom: 12, fontWeight: 600 }}>
                      {itemName}
                    </Typography>
                  )}
                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                    Add special instructions for the kitchen (e.g., "No onions", "Extra spicy", "Well done")
                  </Typography>
                  <TextField
                    label="Kitchen Remark"
                    value={remarkDialogValue}
                    onChange={(e) => setRemarkDialogValue(e.target.value)}
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Enter special instructions for kitchen..."
                    autoFocus
                  />
                </>
              );
            })()}
          </DialogContent>
          <DialogActions style={{ padding: "8px 16px 16px" }}>
            <Button onClick={handleCloseRemarkDialog}>Cancel</Button>
            <Button color="primary" variant="contained" onClick={handleSaveRemarkDialog}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={noteDialogOpen}
          onClose={handleCloseNoteDialog}
          classes={{ paper: classes.dialog }}
        >
          <DialogTitle className={classes.dialogTitle}>Add Note for Invoice</DialogTitle>
          <DialogContent className={classes.dialogContent}>
            {noteDialogTarget && (() => {
              let itemName = "";
              if (noteDialogTarget.type === "session" && activeSession?.items?.[noteDialogTarget.index]) {
                itemName = activeSession.items[noteDialogTarget.index].name || "";
              } else if (noteDialogTarget.type === "cart" && cartItems?.[noteDialogTarget.index]) {
                itemName = cartItems[noteDialogTarget.index].itemName || cartItems[noteDialogTarget.index].name || "";
              }
              return (
                <>
                  {itemName && (
                    <Typography variant="subtitle2" style={{ marginBottom: 12, fontWeight: 600 }}>
                      {itemName}
                    </Typography>
                  )}
                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                    This note prints on the customer's bill (not sent to the kitchen).
                  </Typography>
                  <TextField
                    label="Invoice Note"
                    value={noteDialogValue}
                    onChange={(e) => setNoteDialogValue(e.target.value)}
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Enter a note to print on this item's bill line..."
                    autoFocus
                  />
                </>
              );
            })()}
          </DialogContent>
          <DialogActions style={{ padding: "8px 16px 16px" }}>
            <Button onClick={handleCloseNoteDialog}>Cancel</Button>
            <Button color="primary" variant="contained" onClick={handleSaveNoteDialog}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Table>
    </div>
  );
};

export default CartTable;
